import { RADIUS } from "@elyxeer/ui";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { CoverLibraryDialog } from "~/components/covers/CoverLibraryDialog";

// The API caps an editor's direction at the same length.
const MAX_SUBJECT_LENGTH = 300;

/**
 * One cover control for every editor that has a header image — articles, events,
 * dashboard highlights.
 *
 * The library comes first: most new pieces can wear a photograph that already
 * exists. An uploaded or generated image joins the library for the next editor and
 * lands in this field like a pick, so the save commits all three the same way — on a
 * draft as much as on a published piece. The raw URL field stays for the odd external
 * image.
 */
export function CoverField({
  value,
  onChange,
  label,
  helperText,
  inputTestId,
  testIdPrefix,
  disabled = false,
  busy = false,
  onUpload,
  onGenerate,
}: {
  value: string;
  onChange: (next: string) => void;
  label: string;
  helperText: string;
  /** The URL input's test id, e.g. `editor.cover`. */
  inputTestId: string;
  /** Prefix for the buttons' test ids, e.g. `editor` → `editor.cover-library`. */
  testIdPrefix: string;
  disabled?: boolean;
  /** An upload or generation is in flight. */
  busy?: boolean;
  /** Resolves with the published URL, or null when the caller surfaced an error. */
  onUpload?: (
    file: File,
    options: { watermark: boolean },
  ) => Promise<string | null>;
  onGenerate?: (subject: string | null) => Promise<string | null>;
}) {
  const { t } = useTranslation();
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [watermark, setWatermark] = useState(true);
  // The last image this field published. Publishing no longer touches the saved row,
  // so while it is still the chosen one the editor is reminded that only a save uses it.
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  const url = value.trim();
  const locked = disabled || busy;

  const take = (published: string | null) => {
    if (!published) return;
    setPublishedUrl(published);
    onChange(published);
  };

  const pickFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so choosing the same file again after a failure still fires onChange.
    event.target.value = "";
    if (!file || !onUpload) return;
    void onUpload(file, { watermark }).then(take);
  };

  const generate = () => {
    if (!onGenerate) return;
    setGenerateOpen(false);
    void onGenerate(subject.trim() || null).then(take);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <FormLabel component="legend">{t("covers.field")}</FormLabel>

      <Box
        data-testid={`${testIdPrefix}.cover-preview`}
        sx={{
          width: "100%",
          maxWidth: 400,
          aspectRatio: "16 / 9",
          borderRadius: `${RADIUS.md}px`,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "surface.inset",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {url ? (
          <Box
            component="img"
            src={url}
            alt=""
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Typography
            variant="T3"
            sx={{ color: "text.muted", padding: 2, textAlign: "center" }}
          >
            {t("covers.none")}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<PhotoLibraryIcon />}
          disabled={locked}
          onClick={() => setLibraryOpen(true)}
          data-testid={`${testIdPrefix}.cover-library`}
        >
          {t("covers.choose")}
        </Button>
        {onUpload ? (
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            disabled={locked}
            data-testid={`${testIdPrefix}.cover-upload`}
          >
            {t("covers.upload")}
            <input
              type="file"
              hidden
              // Mirrors the API's allow-list; anything else 422s anyway.
              accept="image/jpeg,image/png,image/webp"
              onChange={pickFile}
              data-testid={`${testIdPrefix}.cover-file`}
            />
          </Button>
        ) : null}
        {onGenerate ? (
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            disabled={locked}
            onClick={() => setGenerateOpen(true)}
            data-testid={`${testIdPrefix}.cover-generate`}
          >
            {t("covers.generate")}
          </Button>
        ) : null}
        {url ? (
          <Button
            color="inherit"
            disabled={locked}
            onClick={() => onChange("")}
            data-testid={`${testIdPrefix}.cover-remove`}
          >
            {t("covers.remove")}
          </Button>
        ) : null}
        {busy ? <CircularProgress size={20} /> : null}
      </Box>

      {onUpload ? (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={watermark}
              onChange={(event) => setWatermark(event.target.checked)}
              data-testid={`${testIdPrefix}.cover-watermark`}
            />
          }
          label={t("covers.watermark")}
        />
      ) : null}
      {url && url === publishedUrl && !busy ? (
        <Typography
          variant="T3"
          sx={{ color: "text.muted" }}
          data-testid={`${testIdPrefix}.cover-published`}
        >
          {t("covers.published")}
        </Typography>
      ) : null}
      {busy && onGenerate ? (
        <Typography variant="T3" sx={{ color: "text.muted" }}>
          {t("covers.working")}
        </Typography>
      ) : null}

      <TextField
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        fullWidth
        size="small"
        helperText={helperText}
        disabled={disabled}
        slotProps={{
          htmlInput: { "data-testid": inputTestId, maxLength: 1024 },
        }}
      />

      {/* Mounted only while open, so an editor that never opens it runs no query. */}
      {libraryOpen ? (
        <CoverLibraryDialog
          open
          currentUrl={url || null}
          onClose={() => setLibraryOpen(false)}
          onPick={onChange}
        />
      ) : null}

      <Dialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("covers.generateTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ marginBottom: 2 }}>
            {t("covers.generateBody")}
          </DialogContentText>
          <TextField
            autoFocus
            label={t("covers.subject")}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder={t("covers.subjectPlaceholder")}
            helperText={t("covers.subjectHelper", {
              count: MAX_SUBJECT_LENGTH - subject.length,
            })}
            multiline
            minRows={2}
            fullWidth
            slotProps={{
              htmlInput: {
                "data-testid": `${testIdPrefix}.cover-subject`,
                maxLength: MAX_SUBJECT_LENGTH,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={generate}
            data-testid={`${testIdPrefix}.cover-generate-confirm`}
          >
            {t("covers.generateConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
